import React, { useState, useMemo } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import ProcurementListView from "./ProcurementListView";
import ProcurementKanbanView from "./ProcurementKanbanView";
import { useProcurement } from "../../context/ProcurementContext";
import PageLayout, { PageHeader, StatsGrid, Toolbar, FilterPanel, TableCard } from "../../components/common/PageLayout";
import ViewSwitcher from "../../components/common/ViewSwitcher";
import { TopPrimaryButton } from "../../components/common/StandardButtons";
import useModuleFilters from "../../hooks/useModuleFilters";
import {
  ShoppingBagIcon,
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
} from "@heroicons/react/24/outline";

const ProcurementPage = () => {
  const [view, setView] = useState("list");
  const [statusFilter, setStatusFilter] = useState("All");
  const [budgetFilter, setBudgetFilter] = useState("All");
  const [projectFilter, setProjectFilter] = useState("All");
  const [search, setSearch] = useState("");

  const {
    showFilter,
    toggleFilters,
    filterOptions,
    loadingFilters,
  } = useModuleFilters("procurement", {
    projects: [],
    statuses: [],
  });

  const navigate = useNavigate();
  const { procurements = [] } = useProcurement();

  // FILTER & SORT
  const filteredData = useMemo(() => {
    let result = (procurements || []).filter((p) => {
      const matchesStatus = statusFilter === "All" || p.status === statusFilter;
      const matchesProject = projectFilter === "All" || p.projectName === projectFilter || String(p.projectId) === String(projectFilter);
      const q = (search || "").toLowerCase();
      const matchesSearch =
        !q ||
        p.projectName?.toLowerCase().includes(q) ||
        p.vendorName?.toLowerCase().includes(q) ||
        p.poNumber?.toLowerCase().includes(q);

      return matchesStatus && matchesProject && matchesSearch;
    });

    if (budgetFilter === "LowToHigh") {
      result.sort((a, b) => (a.budgetAllocated || 0) - (b.budgetAllocated || 0));
    } else if (budgetFilter === "HighToLow") {
      result.sort((a, b) => (b.budgetAllocated || 0) - (a.budgetAllocated || 0));
    }

    return result;
  }, [procurements, statusFilter, projectFilter, search, budgetFilter]);

  // STATS
  const stats = useMemo(() => [
    {
      label: "Total Orders",
      value: procurements.length,
      icon: ShoppingBagIcon,
      variant: "default",
      isActive: statusFilter === "All" || !statusFilter,
      onClick: () => { setStatusFilter("All"); setProjectFilter("All"); },
    },
    {
      label: "Pending",
      value: procurements.filter(p => p.status === "Pending").length,
      icon: ClockIcon,
      variant: "orange",
      isActive: statusFilter === "Pending",
      onClick: () => setStatusFilter(s => s === "Pending" ? "All" : "Pending"),
    },
    {
      label: "Approved",
      value: procurements.filter(p => p.status === "Approved").length,
      icon: CheckCircleIcon,
      variant: "blue",
      isActive: statusFilter === "Approved",
      onClick: () => setStatusFilter(s => s === "Approved" ? "All" : "Approved"),
    },
    {
      label: "Fulfilled",
      value: procurements.filter(p => p.status === "Fulfilled").length,
      icon: TruckIcon,
      variant: "green",
      isActive: statusFilter === "Fulfilled",
      onClick: () => setStatusFilter(s => s === "Fulfilled" ? "All" : "Fulfilled"),
    },
  ], [procurements, statusFilter]);

  const openAddForm = () => navigate("/admin/procurement/add");
  const openEditForm = (id) => navigate(`/admin/procurement/edit/${id}`);
  const handleRefresh = () => window.location.reload();

  return (
    <PageLayout>
      {/* HEADER */}
      <PageHeader
        icon={ShoppingBagIcon}
        iconColor="text-[#E8612C]"
        title="Procurement Management"
        subtitle="Manage purchase orders, requisitions, and vendor fulfillment"
        onRefresh={handleRefresh}
      >
        <TopPrimaryButton
          onClick={openAddForm}
          label="Add Order"
        />
      </PageHeader>

      {/* STATS */}
      <StatsGrid stats={stats} cols={4} />

      {/* TOOLBAR */}
      <Toolbar
        searchTerm={search}
        onSearch={setSearch}
        searchPlaceholder="Search orders, vendors, PO#..."
        tabs={['All', 'Pending', 'Approved', 'Sent', 'Fulfilled']}
        activeTab={statusFilter}
        onTabChange={setStatusFilter}
        onToggleFilters={toggleFilters}
        showFilters={showFilter}
      >
        <ViewSwitcher
          view={view}
          onViewChange={setView}
          availableViews={['list', 'kanban']}
        />
      </Toolbar>

      {/* FILTER PANEL */}
      <FilterPanel
        isOpen={showFilter}
        loading={loadingFilters}
        onClear={() => {
          setStatusFilter("All");
          setBudgetFilter("All");
          setProjectFilter("All");
        }}
      >
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1 flex items-center justify-between">
            <span><T>Project</T></span>
            {loadingFilters && <span className="text-[10px] text-gray-400 animate-pulse font-normal">Loading...</span>}
          </label>
          <select
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Projects</T></option>
            {(filterOptions.projects || []).map((proj) => (
              <option key={proj.id} value={proj.name}>{proj.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">
            <T>Budget Ordering</T>
          </label>
          <select
            value={budgetFilter}
            onChange={(e) => setBudgetFilter(e.target.value)}
            disabled={loadingFilters}
            className="w-full h-8 px-2.5 text-xs bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-[#E8612C]"
          >
            <option value="All"><T>All Budgets</T></option>
            <option value="LowToHigh"><T>Budget: Low → High</T></option>
            <option value="HighToLow"><T>Budget: High → Low</T></option>
          </select>
        </div>
      </FilterPanel>

      {/* CONTENT */}
      <TableCard>
        {view === "list" ? (
          <ProcurementListView data={filteredData} onEdit={openEditForm} />
        ) : (
          <ProcurementKanbanView data={filteredData} onEdit={openEditForm} />
        )}
      </TableCard>
    </PageLayout>
  );
};

export default ProcurementPage;
